import { TrashIcon } from "@heroicons/react/outline";

import { NormalizedStore } from "./types";

import { Button } from "@/components/ui/button";
import LoadingSpinner from "@/components/LoadingSpinner";
import Checkbox from "@/components/ui/checkbox";

interface StoresListProps {
  isLoading: boolean;
  stores: NormalizedStore[];
  activeStoreId: string | null;
  onSelectStore: (id: string) => void;
  isStoreSelected: (id: string) => boolean;
  toggleStoreSelection: (id: string) => void;
  onDeleteStore: (id: string) => void;
  isDeletingStore: (id: string) => boolean;
  emptyMessage: string;
}

export const StoresList: React.FC<StoresListProps> = ({
  isLoading,
  stores,
  activeStoreId,
  onSelectStore,
  isStoreSelected,
  toggleStoreSelection,
  onDeleteStore,
  isDeletingStore,
  emptyMessage,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size={6} />
      </div>
    );
  }

  if (stores.length === 0) {
    return <div className="py-8 text-center text-sm text-gray-500">{emptyMessage}</div>;
  }

  return (
    <>
      {stores.map(store => (
        <div
          key={store.id}
          className={`cursor-pointer rounded-lg border p-3 transition-colors hover:bg-gray-50 ${
            activeStoreId === store.id ? "border-blue-500 bg-blue-50" : ""
          }`}
          onClick={() => onSelectStore(store.id)}
        >
          <div className="flex items-center gap-2">
            <Checkbox
              checked={isStoreSelected(store.id)}
              onChange={e => {
                e.stopPropagation();
                toggleStoreSelection(store.id);
              }}
              onClick={() => undefined}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between">
                <h4 className="truncate text-sm font-medium">{store.displayName}</h4>
                {store.fileCount !== undefined && (
                  <span className="shrink-0 text-xs text-gray-500">{store.fileCount} files</span>
                )}
              </div>
              {store.subtitle && <p className="truncate text-xs text-gray-500">{store.subtitle}</p>}
            </div>
            <Button
              size="sm"
              variant="destructiveOutline"
              className="shrink-0"
              onClick={e => {
                e.stopPropagation();
                if (isStoreSelected(store.id)) {
                  toggleStoreSelection(store.id);
                }
                onDeleteStore(store.id);
              }}
              disabled={isDeletingStore(store.id)}
              title="Delete store"
            >
              {isDeletingStore(store.id) ? (
                <LoadingSpinner size={4} />
              ) : (
                <TrashIcon className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>
      ))}
    </>
  );
};
